import React, { useEffect, useState } from "react";
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter } from "@/components/ui/dialog";
import { Plus, Egg, TrendingUp, Wheat } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { fmt, fmtInt, fmtDate, today } from "@/lib/format";
import { PageHero } from "@/components/premium/UI";
import { usePermissions } from "@/lib/permissions";
import { RowActions } from "@/components/premium/RowActions";

function StatCard({ label, value, sub, icon: Icon, testid }) {
  return (
    <div className="kpi-card" data-testid={testid}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-[11px] uppercase tracking-widest text-muted-foreground">{label}</div>
          <div className="mt-2 font-heading text-2xl font-semibold tracking-tight">{value}</div>
          {sub && <div className="subtle mt-1">{sub}</div>}
        </div>
        <div className="h-10 w-10 rounded-xl bg-deepgreen/10 text-deepgreen flex items-center justify-center"><Icon className="h-5 w-5" /></div>
      </div>
    </div>
  );
}

export default function Poultry() {
  const [batches, setBatches] = useState([]);
  const [daily, setDaily] = useState([]);
  const [sales, setSales] = useState([]);
  const [feed, setFeed] = useState([]);
  const [dash, setDash] = useState(null);

  async function refresh() {
    try {
      const [b, d, s, f, k] = await Promise.all([
        api.get("/poultry/batches"),
        api.get("/poultry/daily"),
        api.get("/poultry/sales"),
        api.get("/poultry/feed"),
        api.get("/poultry/dashboard"),
      ]);
      setBatches(b.data); setDaily(d.data); setSales(s.data); setFeed(f.data); setDash(k.data);
    } catch { toast.error("Could not load Poultry360"); }
  }
  useEffect(() => { refresh(); }, []);

  return (
    <div className="space-y-6" data-testid="poultry-page">
      <PageHero
        eyebrow="Poultry360"
        title="Flock operations"
        subtitle="Batch performance, daily capture, sales and feed."
        moduleKind="poultry"
      />

      {dash && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
          <StatCard label="Current Birds" value={fmtInt(dash.total_birds)} icon={Egg} testid="poultry-kpi-birds" />
          <StatCard label="Ready for Sale" value={fmtInt(dash.ready_for_sale)} icon={TrendingUp} testid="poultry-kpi-ready" />
          <StatCard label="Feed Balance" value={`${fmt(dash.feed_balance_kg, 0)} kg`} icon={Wheat} testid="poultry-kpi-feed" />
          <StatCard label="Revenue (Month)" value={fmt(dash.revenue_month)} sub={`Outstanding ${fmt(dash.outstanding_payments)}`} icon={TrendingUp} testid="poultry-kpi-rev" />
        </div>
      )}

      <Tabs defaultValue="batches" className="mt-4">
        <TabsList className="grid grid-cols-4 max-w-2xl">
          <TabsTrigger value="batches" data-testid="poultry-tab-batches">Batches</TabsTrigger>
          <TabsTrigger value="daily" data-testid="poultry-tab-daily">Daily capture</TabsTrigger>
          <TabsTrigger value="sales" data-testid="poultry-tab-sales">Sales</TabsTrigger>
          <TabsTrigger value="feed" data-testid="poultry-tab-feed">Feed</TabsTrigger>
        </TabsList>

        <TabsContent value="batches" className="mt-6">
          <BatchesSection batches={batches} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="daily" className="mt-6">
          <DailySection batches={batches} daily={daily} onChanged={refresh} />
        </TabsContent>
        <TabsContent value="sales" className="mt-6">
          <SalesSection batches={batches} sales={sales} onChanged={refresh} />
          <McSalesView />
        </TabsContent>
        <TabsContent value="feed" className="mt-6">
          <FeedSection feed={feed} onChanged={refresh} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

function BatchesSection({ batches, onChanged }) {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_ops:write");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    batch_number: "", breed: "Broiler", supplier: "", placement_date: today(),
    num_received: 500, target_weight: 2.0, feed_type: "Starter", house: "House A",
    supervisor: "", notes: "", status: "active",
  });
  async function submit() {
    try {
      await api.post("/poultry/batches", { ...form, num_received: Number(form.num_received) });
      toast.success("Batch created");
      setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Batches</CardTitle>
          <CardDescription>{batches.length} batch(es)</CardDescription>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canWrite ? (
            <DialogTrigger asChild>
              <Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-batch-btn"><Plus className="h-4 w-4 mr-2" />New batch</Button>
            </DialogTrigger>
          ) : (
            <Button className="opacity-50 cursor-not-allowed" disabled title="Read-only in this module" data-testid="new-batch-btn-disabled"><Plus className="h-4 w-4 mr-2" />New batch</Button>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>New poultry batch</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Batch #"><Input value={form.batch_number} onChange={(e) => setForm({ ...form, batch_number: e.target.value })} data-testid="batch-number-input" /></FormField>
              <FormField label="Breed"><Input value={form.breed} onChange={(e) => setForm({ ...form, breed: e.target.value })} /></FormField>
              <FormField label="Placement date"><Input type="date" value={form.placement_date} onChange={(e) => setForm({ ...form, placement_date: e.target.value })} /></FormField>
              <FormField label="Received"><Input type="number" value={form.num_received} onChange={(e) => setForm({ ...form, num_received: e.target.value })} data-testid="batch-count-input" /></FormField>
              <FormField label="Supplier"><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></FormField>
              <FormField label="House"><Input value={form.house} onChange={(e) => setForm({ ...form, house: e.target.value })} /></FormField>
              <FormField label="Feed type"><Input value={form.feed_type} onChange={(e) => setForm({ ...form, feed_type: e.target.value })} /></FormField>
              <FormField label="Supervisor"><Input value={form.supervisor} onChange={(e) => setForm({ ...form, supervisor: e.target.value })} /></FormField>
              <FormField label="Status">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="active">Active</SelectItem>
                    <SelectItem value="ready">Ready for sale</SelectItem>
                    <SelectItem value="closed">Closed</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Target weight (kg)"><Input type="number" step="0.1" value={form.target_weight} onChange={(e) => setForm({ ...form, target_weight: Number(e.target.value) })} /></FormField>
              <div className="col-span-2"><FormField label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormField></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="batch-save-btn">Save batch</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Batch #</TableHead><TableHead>Breed</TableHead><TableHead>Placed</TableHead>
              <TableHead>Received</TableHead><TableHead>Current</TableHead><TableHead>Mortality</TableHead>
              <TableHead>Status</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {batches.map((b) => (
              <TableRow key={b.id} data-testid={`batch-row-${b.batch_number}`}>
                <TableCell className="font-medium">{b.batch_number}</TableCell>
                <TableCell>{b.breed}</TableCell>
                <TableCell>{fmtDate(b.placement_date)}</TableCell>
                <TableCell>{fmtInt(b.num_received)}</TableCell>
                <TableCell>{fmtInt(b.current_birds)}</TableCell>
                <TableCell>{fmtInt(b.mortality)}</TableCell>
                <TableCell><Badge variant={b.status === "ready" ? "default" : "outline"}>{b.status}</Badge></TableCell>
                <TableCell className="text-right">
                  <RowActions
                    row={b} canWrite={canWrite}
                    basePath="/poultry/batches" resource="batch"
                    editableFields={["batch_number", "breed", "placement_date", "num_received", "current_birds", "mortality", "status", "supplier", "house", "feed_type", "supervisor", "target_weight", "notes"]}
                    renderEditForm={(s, setS) => (
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Batch #"><Input value={s.batch_number ?? ""} onChange={(e) => setS({ ...s, batch_number: e.target.value })} /></FormField>
                        <FormField label="Breed"><Input value={s.breed ?? ""} onChange={(e) => setS({ ...s, breed: e.target.value })} /></FormField>
                        <FormField label="Placement date"><Input type="date" value={s.placement_date ?? ""} onChange={(e) => setS({ ...s, placement_date: e.target.value })} /></FormField>
                        <FormField label="Received"><Input type="number" value={s.num_received ?? 0} onChange={(e) => setS({ ...s, num_received: Number(e.target.value) })} /></FormField>
                        <FormField label="Current birds"><Input type="number" value={s.current_birds ?? 0} onChange={(e) => setS({ ...s, current_birds: Number(e.target.value) })} /></FormField>
                        <FormField label="Mortality"><Input type="number" value={s.mortality ?? 0} onChange={(e) => setS({ ...s, mortality: Number(e.target.value) })} /></FormField>
                        <FormField label="Status">
                          <Select value={s.status ?? "active"} onValueChange={(v) => setS({ ...s, status: v })}>
                            <SelectTrigger><SelectValue /></SelectTrigger>
                            <SelectContent>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="ready">Ready for sale</SelectItem>
                              <SelectItem value="closed">Closed</SelectItem>
                            </SelectContent>
                          </Select>
                        </FormField>
                        <FormField label="Supplier"><Input value={s.supplier ?? ""} onChange={(e) => setS({ ...s, supplier: e.target.value })} /></FormField>
                        <FormField label="House"><Input value={s.house ?? ""} onChange={(e) => setS({ ...s, house: e.target.value })} /></FormField>
                        <FormField label="Feed type"><Input value={s.feed_type ?? ""} onChange={(e) => setS({ ...s, feed_type: e.target.value })} /></FormField>
                        <div className="col-span-2"><FormField label="Notes"><Textarea value={s.notes ?? ""} onChange={(e) => setS({ ...s, notes: e.target.value })} /></FormField></div>
                      </div>
                    )}
                    onChanged={onChanged}
                  />
                </TableCell>
              </TableRow>
            ))}
            {batches.length === 0 && <TableRow><TableCell colSpan={8} className="text-center subtle py-8">No batches yet. Add your first placement.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function DailySection({ batches, daily, onChanged }) {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_ops:write");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    batch_id: "", capture_date: today(), mortality: 0, feed_consumed_kg: 0,
    water_consumed_l: 0, house_condition: "good", temperature_c: 24, humidity_pct: 55,
    bird_condition: "healthy", medication: "", vaccination: "", comments: "",
  });
  async function submit() {
    if (!form.batch_id) { toast.error("Choose a batch"); return; }
    try {
      await api.post("/poultry/daily", { ...form, mortality: Number(form.mortality), feed_consumed_kg: Number(form.feed_consumed_kg), water_consumed_l: Number(form.water_consumed_l), temperature_c: Number(form.temperature_c), humidity_pct: Number(form.humidity_pct) });
      toast.success("Daily capture saved"); setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div><CardTitle>Daily capture</CardTitle><CardDescription>{daily.length} entries</CardDescription></div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canWrite ? (
            <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-daily-btn"><Plus className="h-4 w-4 mr-2" />Capture today</Button></DialogTrigger>
          ) : (
            <Button className="opacity-50 cursor-not-allowed" disabled title="Read-only in this module" data-testid="new-daily-btn-disabled"><Plus className="h-4 w-4 mr-2" />Capture today</Button>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>Poultry daily capture</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Batch">
                <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                  <SelectTrigger data-testid="daily-batch-select"><SelectValue placeholder="Select batch" /></SelectTrigger>
                  <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.batch_number}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Date"><Input type="date" value={form.capture_date} onChange={(e) => setForm({ ...form, capture_date: e.target.value })} /></FormField>
              <FormField label="Mortality"><Input type="number" value={form.mortality} onChange={(e) => setForm({ ...form, mortality: e.target.value })} /></FormField>
              <FormField label="Feed (kg)"><Input type="number" value={form.feed_consumed_kg} onChange={(e) => setForm({ ...form, feed_consumed_kg: e.target.value })} /></FormField>
              <FormField label="Water (L)"><Input type="number" value={form.water_consumed_l} onChange={(e) => setForm({ ...form, water_consumed_l: e.target.value })} /></FormField>
              <FormField label="Temp (°C)"><Input type="number" step="0.1" value={form.temperature_c} onChange={(e) => setForm({ ...form, temperature_c: e.target.value })} /></FormField>
              <FormField label="Humidity %"><Input type="number" value={form.humidity_pct} onChange={(e) => setForm({ ...form, humidity_pct: e.target.value })} /></FormField>
              <FormField label="Bird condition"><Input value={form.bird_condition} onChange={(e) => setForm({ ...form, bird_condition: e.target.value })} /></FormField>
              <FormField label="Medication"><Input value={form.medication} onChange={(e) => setForm({ ...form, medication: e.target.value })} /></FormField>
              <FormField label="Vaccination"><Input value={form.vaccination} onChange={(e) => setForm({ ...form, vaccination: e.target.value })} /></FormField>
              <div className="col-span-2"><FormField label="Comments"><Textarea value={form.comments} onChange={(e) => setForm({ ...form, comments: e.target.value })} /></FormField></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="daily-save-btn">Save capture</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Batch</TableHead><TableHead>Mortality</TableHead>
            <TableHead>Feed (kg)</TableHead><TableHead>Water (L)</TableHead><TableHead>Condition</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {daily.map((d) => {
              const b = batches.find((x) => x.id === d.batch_id);
              return (
                <TableRow key={d.id} data-testid={`daily-row-${d.id}`}>
                  <TableCell>{fmtDate(d.capture_date)}</TableCell>
                  <TableCell>{b?.batch_number || "—"}</TableCell>
                  <TableCell>{fmtInt(d.mortality)}</TableCell>
                  <TableCell>{fmt(d.feed_consumed_kg, 1)}</TableCell>
                  <TableCell>{fmt(d.water_consumed_l, 1)}</TableCell>
                  <TableCell>{d.bird_condition}</TableCell>
                  <TableCell className="text-right">
                    <RowActions
                      row={d} canWrite={canWrite}
                      basePath="/poultry/daily" resource="daily-capture"
                      editableFields={["capture_date", "mortality", "feed_consumed_kg", "water_consumed_l", "temperature_c", "humidity_pct", "bird_condition", "medication", "vaccination", "comments"]}
                      renderEditForm={(s, setS) => (
                        <div className="grid grid-cols-2 gap-3">
                          <FormField label="Date"><Input type="date" value={s.capture_date ?? ""} onChange={(e) => setS({ ...s, capture_date: e.target.value })} /></FormField>
                          <FormField label="Mortality"><Input type="number" value={s.mortality ?? 0} onChange={(e) => setS({ ...s, mortality: Number(e.target.value) })} data-testid="edit-mortality-input" /></FormField>
                          <FormField label="Feed (kg)"><Input type="number" value={s.feed_consumed_kg ?? 0} onChange={(e) => setS({ ...s, feed_consumed_kg: Number(e.target.value) })} /></FormField>
                          <FormField label="Water (L)"><Input type="number" value={s.water_consumed_l ?? 0} onChange={(e) => setS({ ...s, water_consumed_l: Number(e.target.value) })} /></FormField>
                          <FormField label="Temp (°C)"><Input type="number" step="0.1" value={s.temperature_c ?? 0} onChange={(e) => setS({ ...s, temperature_c: Number(e.target.value) })} /></FormField>
                          <FormField label="Humidity %"><Input type="number" value={s.humidity_pct ?? 0} onChange={(e) => setS({ ...s, humidity_pct: Number(e.target.value) })} /></FormField>
                          <FormField label="Bird condition"><Input value={s.bird_condition ?? ""} onChange={(e) => setS({ ...s, bird_condition: e.target.value })} /></FormField>
                          <FormField label="Medication"><Input value={s.medication ?? ""} onChange={(e) => setS({ ...s, medication: e.target.value })} /></FormField>
                          <FormField label="Vaccination"><Input value={s.vaccination ?? ""} onChange={(e) => setS({ ...s, vaccination: e.target.value })} /></FormField>
                          <div className="col-span-2"><FormField label="Comments"><Textarea value={s.comments ?? ""} onChange={(e) => setS({ ...s, comments: e.target.value })} /></FormField></div>
                        </div>
                      )}
                      onChanged={onChanged}
                    />
                  </TableCell>
                </TableRow>
              );
            })}
            {daily.length === 0 && <TableRow><TableCell colSpan={7} className="text-center subtle py-8">No daily captures yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function McSalesView() {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_sales:write");
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [nonce, setNonce] = useState(0);
  useEffect(() => {
    let alive = true;
    api.get("/sales/poultry-sales")
      .then(r => { if (alive) setRows((r.data || []).filter(x => x.status !== "reversal")); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [nonce]);
  const money = (n, c) => {
    const v = Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return c === "USD" ? `$${v}` : `R${v}`;
  };
  return (
    <Card className="rounded-2xl mt-6" data-testid="poultry-mc-sales-view">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div>
          <CardTitle>Multi-currency sales (from Sales & Cash)</CardTitle>
          <CardDescription>Read-only capture (Sales & Cash is authoritative). Authorised users can reverse an incorrect sale — birds are restored to the original batch automatically. {rows.length} record(s).</CardDescription>
        </div>
        <Badge variant="secondary">Sales & Cash</Badge>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="animate-pulse space-y-2">{[1,2,3].map(i => <div key={i} className="h-9 bg-muted/40 rounded" />)}</div>
        ) : rows.length === 0 ? (
          <div className="text-center text-sm text-muted-foreground py-8">No multi-currency poultry sales yet.</div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Date</TableHead>
                <TableHead>Invoice</TableHead>
                <TableHead>Batch</TableHead>
                <TableHead>Customer</TableHead>
                <TableHead className="text-right">Birds</TableHead>
                <TableHead>Currency</TableHead>
                <TableHead className="text-right">Total</TableHead>
                <TableHead className="text-right">Outstanding</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map(r => (
                <TableRow key={r.id} data-testid={`mc-sale-row-${r.invoice_number}`}>
                  <TableCell>{r.sale_date}</TableCell>
                  <TableCell className="font-mono text-xs">{r.invoice_number}</TableCell>
                  <TableCell>{r.poultry_batch_number || "—"}</TableCell>
                  <TableCell>{r.customer || "—"}</TableCell>
                  <TableCell className="text-right">{r.quantity}</TableCell>
                  <TableCell>{r.transaction_currency}</TableCell>
                  <TableCell className="text-right font-medium">{money(r.total, r.transaction_currency)}</TableCell>
                  <TableCell className="text-right">{Number(r.balance || 0) > 0.005 ? <span className="text-destructive font-semibold">{money(r.balance, r.transaction_currency)}</span> : "—"}</TableCell>
                  <TableCell><Badge variant={r.payment_status === "paid" || String(r.payment_status).toUpperCase() === "PAID" ? "default" : "outline"}>{r.payment_status || "—"}</Badge></TableCell>
                  <TableCell className="text-right">
                    <RowActions
                      row={r} canWrite={canWrite}
                      basePath="/sales/transactions" resource={`mc-sale-${r.invoice_number}`}
                      actions={["reverse"]}
                      reverseLabel="Reverse sale"
                      onChanged={() => setNonce(n => n + 1)}
                    />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function SalesSection({ batches, sales, onChanged }) {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_sales:write");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    batch_id: "", sale_date: today(), customer: "", birds_sold: 10, avg_weight_kg: 1.8,
    price_per_kg: 3.5, payment_status: "paid", amount_paid: null, delivery: "", receipt_ref: "", notes: "",
  });
  async function submit() {
    try {
      await api.post("/poultry/sales", { ...form, birds_sold: Number(form.birds_sold), avg_weight_kg: Number(form.avg_weight_kg), price_per_kg: Number(form.price_per_kg), amount_paid: form.amount_paid === null || form.amount_paid === "" ? null : Number(form.amount_paid) });
      toast.success("Sale recorded"); setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div><CardTitle>Sales</CardTitle><CardDescription>{sales.length} sale(s)</CardDescription></div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canWrite ? (
            <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-sale-btn"><Plus className="h-4 w-4 mr-2" />Record sale</Button></DialogTrigger>
          ) : (
            <Button className="opacity-50 cursor-not-allowed" disabled title="Read-only in this module" data-testid="new-sale-btn-disabled"><Plus className="h-4 w-4 mr-2" />Record sale</Button>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>Record poultry sale</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Batch">
                <Select value={form.batch_id} onValueChange={(v) => setForm({ ...form, batch_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Optional" /></SelectTrigger>
                  <SelectContent>{batches.map((b) => <SelectItem key={b.id} value={b.id}>{b.batch_number}</SelectItem>)}</SelectContent>
                </Select>
              </FormField>
              <FormField label="Date"><Input type="date" value={form.sale_date} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} /></FormField>
              <FormField label="Customer"><Input value={form.customer} onChange={(e) => setForm({ ...form, customer: e.target.value })} data-testid="sale-customer-input" /></FormField>
              <FormField label="Birds sold"><Input type="number" value={form.birds_sold} onChange={(e) => setForm({ ...form, birds_sold: e.target.value })} /></FormField>
              <FormField label="Avg weight (kg)"><Input type="number" step="0.1" value={form.avg_weight_kg} onChange={(e) => setForm({ ...form, avg_weight_kg: e.target.value })} /></FormField>
              <FormField label="Price / kg"><Input type="number" step="0.01" value={form.price_per_kg} onChange={(e) => setForm({ ...form, price_per_kg: e.target.value })} /></FormField>
              <FormField label="Payment">
                <Select value={form.payment_status} onValueChange={(v) => setForm({ ...form, payment_status: v })}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="paid">Paid</SelectItem><SelectItem value="partial">Partial</SelectItem><SelectItem value="unpaid">Unpaid</SelectItem>
                  </SelectContent>
                </Select>
              </FormField>
              <FormField label="Amount paid"><Input type="number" value={form.amount_paid ?? ""} onChange={(e) => setForm({ ...form, amount_paid: e.target.value })} /></FormField>
              <FormField label="Receipt ref"><Input value={form.receipt_ref} onChange={(e) => setForm({ ...form, receipt_ref: e.target.value })} /></FormField>
              <FormField label="Delivery"><Input value={form.delivery} onChange={(e) => setForm({ ...form, delivery: e.target.value })} /></FormField>
              <div className="col-span-2"><FormField label="Notes"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} /></FormField></div>
            </div>
            <DialogFooter><Button onClick={submit} data-testid="sale-save-btn">Save sale</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Date</TableHead><TableHead>Customer</TableHead><TableHead>Birds</TableHead>
            <TableHead>Avg wt</TableHead><TableHead>Total</TableHead><TableHead>Balance</TableHead><TableHead>Status</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {sales.map((s) => (
              <TableRow key={s.id}>
                <TableCell>{fmtDate(s.sale_date)}</TableCell>
                <TableCell>{s.customer}</TableCell>
                <TableCell>{fmtInt(s.birds_sold)}</TableCell>
                <TableCell>{fmt(s.avg_weight_kg, 1)}</TableCell>
                <TableCell className="font-medium">{fmt(s.total)}</TableCell>
                <TableCell>{fmt(s.balance)}</TableCell>
                <TableCell><Badge variant={s.payment_status === "paid" ? "default" : "outline"}>{s.payment_status}</Badge></TableCell>
              </TableRow>
            ))}
            {sales.length === 0 && <TableRow><TableCell colSpan={7} className="text-center subtle py-8">No sales yet.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function FeedSection({ feed, onChanged }) {
  const { has } = usePermissions();
  const canWrite = has("data:poultry_ops:write");
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({
    supplier: "", feed_type: "Broiler Starter", quantity_received_kg: 500,
    unit_cost: 0.6, received_date: today(), minimum_level_kg: 100,
  });
  async function submit() {
    try {
      await api.post("/poultry/feed", { ...form, quantity_received_kg: Number(form.quantity_received_kg), unit_cost: Number(form.unit_cost), minimum_level_kg: Number(form.minimum_level_kg) });
      toast.success("Feed stock added"); setOpen(false); onChanged();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  }
  return (
    <Card className="rounded-2xl">
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <div><CardTitle>Feed stock</CardTitle><CardDescription>{feed.length} deliveries</CardDescription></div>
        <Dialog open={open} onOpenChange={setOpen}>
          {canWrite ? (
            <DialogTrigger asChild><Button className="bg-deepgreen hover:bg-deepgreen/90" data-testid="new-feed-btn"><Plus className="h-4 w-4 mr-2" />Add feed</Button></DialogTrigger>
          ) : (
            <Button className="opacity-50 cursor-not-allowed" disabled title="Read-only in this module" data-testid="new-feed-btn-disabled"><Plus className="h-4 w-4 mr-2" />Add feed</Button>
          )}
          <DialogContent>
            <DialogHeader><DialogTitle>Add feed delivery</DialogTitle></DialogHeader>
            <div className="grid grid-cols-2 gap-4">
              <FormField label="Feed type"><Input value={form.feed_type} onChange={(e) => setForm({ ...form, feed_type: e.target.value })} /></FormField>
              <FormField label="Supplier"><Input value={form.supplier} onChange={(e) => setForm({ ...form, supplier: e.target.value })} /></FormField>
              <FormField label="Quantity (kg)"><Input type="number" value={form.quantity_received_kg} onChange={(e) => setForm({ ...form, quantity_received_kg: e.target.value })} /></FormField>
              <FormField label="Unit cost"><Input type="number" step="0.01" value={form.unit_cost} onChange={(e) => setForm({ ...form, unit_cost: e.target.value })} /></FormField>
              <FormField label="Received"><Input type="date" value={form.received_date} onChange={(e) => setForm({ ...form, received_date: e.target.value })} /></FormField>
              <FormField label="Minimum (kg)"><Input type="number" value={form.minimum_level_kg} onChange={(e) => setForm({ ...form, minimum_level_kg: e.target.value })} /></FormField>
            </div>
            <DialogFooter><Button onClick={submit}>Save</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>Received</TableHead><TableHead>Type</TableHead><TableHead>Supplier</TableHead>
            <TableHead>Received (kg)</TableHead><TableHead>Balance (kg)</TableHead><TableHead>Minimum</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {feed.map((f) => (
              <TableRow key={f.id} data-testid={`feed-row-${f.id}`}>
                <TableCell>{fmtDate(f.received_date)}</TableCell>
                <TableCell>{f.feed_type}</TableCell>
                <TableCell>{f.supplier}</TableCell>
                <TableCell>{fmt(f.quantity_received_kg, 0)}</TableCell>
                <TableCell className="font-medium">{fmt(f.current_balance_kg, 0)}</TableCell>
                <TableCell>{fmt(f.minimum_level_kg, 0)}</TableCell>
                <TableCell className="text-right">
                  <RowActions
                    row={f} canWrite={canWrite}
                    basePath="/poultry/feed" resource="feed"
                    editableFields={["feed_type", "supplier", "quantity_received_kg", "unit_cost", "received_date", "minimum_level_kg"]}
                    renderEditForm={(s, setS) => (
                      <div className="grid grid-cols-2 gap-3">
                        <FormField label="Feed type"><Input value={s.feed_type ?? ""} onChange={(e) => setS({ ...s, feed_type: e.target.value })} /></FormField>
                        <FormField label="Supplier"><Input value={s.supplier ?? ""} onChange={(e) => setS({ ...s, supplier: e.target.value })} /></FormField>
                        <FormField label="Quantity (kg)"><Input type="number" value={s.quantity_received_kg ?? 0} onChange={(e) => setS({ ...s, quantity_received_kg: Number(e.target.value) })} /></FormField>
                        <FormField label="Unit cost"><Input type="number" step="0.01" value={s.unit_cost ?? 0} onChange={(e) => setS({ ...s, unit_cost: Number(e.target.value) })} /></FormField>
                        <FormField label="Received"><Input type="date" value={s.received_date ?? ""} onChange={(e) => setS({ ...s, received_date: e.target.value })} /></FormField>
                        <FormField label="Minimum (kg)"><Input type="number" value={s.minimum_level_kg ?? 0} onChange={(e) => setS({ ...s, minimum_level_kg: Number(e.target.value) })} /></FormField>
                      </div>
                    )}
                    onChanged={onChanged}
                  />
                </TableCell>
              </TableRow>
            ))}
            {feed.length === 0 && <TableRow><TableCell colSpan={7} className="text-center subtle py-8">No feed stock recorded.</TableCell></TableRow>}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}

function FormField({ label, children }) {
  return (
    <div className="space-y-1.5">
      <Label className="text-xs uppercase tracking-widest text-muted-foreground">{label}</Label>
      {children}
    </div>
  );
}
